import {Row, Col, Form} from 'react-bootstrap';
import * as yup from 'yup';
import BaseSettingsForm from './BaseSettingsForm';
import Button from '@/components/ui/Button';
import {
    AsyncSelectField,
    SelectField,
    RichTextEditorField,
    DatePickerField,
    FileUploadField,
    MultiSelectField,
    SwitchField,
    RangeField
} from './fields';
import {FormFieldConfig, QuickFormConfig} from './types';
import i18n from 'i18n';

const QuickSettingsForm = <T extends Record<string, any>>({
    entityId,
    crudConfig,
    fields,
    initialValues,
    onSave,
    customValidation,
    customErrorHandling,
    customSuccessHandling,
    customSubmissionHandler,
    additionalStoreActions,
}: QuickFormConfig<T>) => {
    // Generate validation schema from fields if not provided
    const validationSchema = customValidation || yup.object().shape(
        fields.reduce((schema, field) => {
            let fieldValidation: yup.Schema<any> = yup.mixed();

            if (field.type === 'text' || field.type === 'email' || field.type === 'textarea' || field.type === 'richText') {
                let stringValidation = yup.string();
                if (field.type === 'email') {
                    stringValidation = stringValidation.email(i18n.t('jt_action_invalid_email.text'));
                }
                fieldValidation = stringValidation;
            } else if (field.type === 'number' || field.type === 'range') {
                let numberValidation = yup.number();
                if (field.min !== undefined) {
                    numberValidation = numberValidation.min(field.min);
                }
                if (field.max !== undefined) {
                    numberValidation = numberValidation.max(field.max);
                }
                fieldValidation = numberValidation;
            } else if (field.type === 'checkbox' || field.type === 'switch') {
                fieldValidation = yup.boolean();
            } else if (field.type === 'date') {
                fieldValidation = yup.date().nullable();
            } else if (field.type === 'select' || field.type === 'asyncSelect') {
                if (field.isMulti) {
                    fieldValidation = yup.array().of(
                        yup.object({
                            value: yup.mixed().required(),
                            label: yup.string().required(),
                        })
                    );
                } else {
                    fieldValidation = yup.object({
                        value: yup.mixed().required(),
                        label: yup.string().required(),
                    }).nullable();
                }
            } else if (field.type === 'multiSelect') {
                fieldValidation = yup.array().of(
                    yup.object({
                        value: yup.mixed().required(),
                        label: yup.string().required(),
                    })
                ).nullable();
                if (field.maxSelectedItems) {
                    fieldValidation = (fieldValidation as yup.ArraySchema<any, any>).max(field.maxSelectedItems);
                }
            } else if (field.type === 'fileUpload') {
                if (field.multiple) {
                    fieldValidation = yup.array().of(yup.mixed()).nullable();
                    if (field.maxFiles) {
                        fieldValidation = (fieldValidation as yup.ArraySchema<any, any>).max(field.maxFiles);
                    }
                } else {
                    fieldValidation = yup.mixed().nullable();
                }
            }

            if (field.required) {
                fieldValidation = fieldValidation.required(
                    i18n.t('jt_required_field.text', {field: field.label})
                );
            }

            if (field.validation) {
                fieldValidation = field.validation;
            }

            return {...schema, [field.name]: fieldValidation};
        }, {} as Record<string, yup.Schema<any>>)
    );

    const renderField = (field: FormFieldConfig, formik: any) => {
        const hasError = formik.touched[field.name] && formik.errors[field.name];

        switch (field.type) {
            case 'asyncSelect':
                return (
                    <AsyncSelectField
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        loadOptions={field.loadOptions!}
                        isMulti={field.isMulti}
                        placeholder={field.placeholder}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        enableSelectAll={field.enableSelectAll}
                        debounceMs={field.debounceMs}
                    />
                );

            case 'richText':
                return (
                    <RichTextEditorField
                        name={field.name}
                        value={formik.values[field.name] || ''}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        placeholder={field.placeholder}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        minHeight={field.minHeight}
                        customHeight={field.customHeight}
                    />
                );

            case 'select':
                if (field.options && field.options.length > 0) {
                    // Find the matching option object based on the raw value
                    const selectedOption = field.options.find((option: any) => option.value === formik.values[field.name]) || null;
          
                    return (
                        <SelectField
                            name={field.name}
                            value={selectedOption}
                            onChange={(selectedOption: any) => {
                                formik.setFieldValue(field.name, selectedOption ? selectedOption.value : null);
                            }}
                            options={field.options}
                            placeholder={field.placeholder}
                            isRequired={field.required}
                            error={formik.errors[field.name]}
                            touched={formik.touched[field.name]}
                        />
                    );
                }
                // Fallback to native select for simple cases
                return (
                    <Form.Select
                        name={field.name}
                        value={formik.values[field.name] || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        isInvalid={hasError}
                    >
                        <option value="">{i18n.t('jt_select_option.text')}</option>
                        {field.options && field.options.map((option: any) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                );

            case 'checkbox':
                return (
                    <Form.Check
                        type="checkbox"
                        name={field.name}
                        checked={formik.values[field.name] || false}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        label={field.label}
                    />
                );

            case 'textarea':
                return (
                    <Form.Control
                        as="textarea"
                        rows={3}
                        name={field.name}
                        value={formik.values[field.name] || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={field.placeholder}
                        isInvalid={hasError}
                        disabled={field.disabled}
                    />
                );

            case 'number':
                return (
                    <Form.Control
                        type="number"
                        name={field.name}
                        value={formik.values[field.name] || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={field.placeholder}
                        isInvalid={hasError}
                        disabled={field.disabled}
                    />
                );

            case 'date':
                return (
                    <DatePickerField
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        placeholder={field.placeholder}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        dateFormat={field.dateFormat}
                        isClearable={field.isClearable}
                        disabled={field.disabled}
                    />
                );

            case 'fileUpload':
                return (
                    <FileUploadField
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        placeholder={field.placeholder}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        multiple={field.multiple}
                        accept={field.accept}
                        maxSize={field.maxSize}
                        maxFiles={field.maxFiles}
                        disabled={field.disabled}
                    />
                );

            case 'multiSelect':
                return (
                    <MultiSelectField
                        name={field.name}
                        value={formik.values[field.name]}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        options={field.options || []}
                        placeholder={field.placeholder}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        disabled={field.disabled}
                        isSearchable={field.isSearchable}
                        closeMenuOnSelect={field.closeMenuOnSelect}
                        enableSelectAll={field.enableSelectAll}
                        maxSelectedItems={field.maxSelectedItems}
                    />
                );

            case 'switch':
                return (
                    <SwitchField
                        name={field.name}
                        value={formik.values[field.name] || false}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        label={field.label}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        disabled={field.disabled}
                        helpText={field.helpText}
                        size={field.size}
                        variant={field.variant}
                    />
                );

            case 'range':
                return (
                    <RangeField
                        name={field.name}
                        value={formik.values[field.name] || field.min || 0}
                        onChange={(value) => formik.setFieldValue(field.name, value)}
                        isRequired={field.required}
                        error={formik.errors[field.name]}
                        touched={formik.touched[field.name]}
                        disabled={field.disabled}
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        showValue={field.showValue}
                        showMinMax={field.showMinMax}
                        unit={field.unit}
                        marks={field.marks}
                    />
                );

            default: // text, email
                return (
                    <Form.Control
                        type={field.type}
                        name={field.name}
                        value={formik.values[field.name] || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={field.placeholder}
                        isInvalid={hasError}
                        disabled={field.disabled}
                    />
                );
        }
    };

    return (
        <BaseSettingsForm
            entityId={entityId}
            resourceUrl={crudConfig.resourceUrl}
            storeActionPath={crudConfig.storeActionPath}
            validationSchema={validationSchema}
            initialValues={initialValues}
            successMessageKey={crudConfig.successMessageKey}
            onSave={onSave}
            customErrorHandling={customErrorHandling}
            customSuccessHandling={customSuccessHandling}
            customSubmissionHandler={customSubmissionHandler}
            additionalStoreActions={additionalStoreActions}
        >
            {(formik, loading) => (
                <>
                    {fields.map(field => (
                        <Row key={field.name} className="mb-3">
                            <Col xs={12}>
                                {field.type !== 'checkbox' && field.type !== 'switch' && (
                                    <Form.Group className="mb-3" controlId={field.name}>
                                        <Form.Label>
                                            {field.label}
                                            {field.required && <span className="text-danger"> *</span>}
                                        </Form.Label>
                                        {renderField(field, formik)}
                                        {field.helpText && (
                                            <Form.Text className="text-muted">
                                                {field.helpText}
                                            </Form.Text>
                                        )}
                                        <Form.Control.Feedback type="invalid">
                                            {formik.errors[field.name]}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                                {(field.type === 'checkbox' || field.type === 'switch') && (
                                    <Form.Group className="mb-3" controlId={field.name}>
                                        {renderField(field, formik)}
                                        {field.helpText && field.type === 'checkbox' && (
                                            <Form.Text className="text-muted">
                                                {field.helpText}
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                )}
                            </Col>
                        </Row>
                    ))
                    }

                    <Row>
                        <Col xs={12} className="d-flex justify-content-end">
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={formik.isSubmitting}
                                loading={formik.isSubmitting}
                                data-cy="quick-settings-btn">
                                {entityId ? i18n.t('jt_update.text') : i18n.t('jt_update.text')}
                            </Button>
                        </Col>
                    </Row>
                </>
            )}
        </BaseSettingsForm >
    );
};

export default QuickSettingsForm;
